import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TextField,
} from '@mui/material';
import { CloudUpload, InfoOutlined, Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import { sttApi } from '../../api/sttApi';
import { getMaxFileSize } from '../../config';
import { getErrorMessage } from '../../utils/helpers';

const getMediaDurationSeconds = (file) => {
    if (!file || !file.type) {
        return Promise.resolve(null);
    }
    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');
    if (!isVideo && !isAudio) {
        return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const media = document.createElement(isVideo ? 'video' : 'audio');
        media.preload = 'metadata';

        const cleanup = () => {
            URL.revokeObjectURL(objectUrl);
        };

        media.onloadedmetadata = () => {
            const duration = media.duration;
            cleanup();
            if (Number.isFinite(duration) && duration > 0) {
                resolve(duration);
            } else {
                resolve(null);
            }
        };

        media.onerror = () => {
            cleanup();
            reject(new Error('Failed to read media metadata'));
        };

        media.src = objectUrl;
    });
};

const FileUpload = ({ apiKeyId, onApiKeyIdChange, onUpload, activeJobs, maxConcurrent }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState('');
    const [apiKeys, setApiKeys] = useState([]);
    const [wordTimestamps, setWordTimestamps] = useState(false);
    const [enableDiarization, setEnableDiarization] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const hasInitialized = useRef(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const maxFileSize = getMaxFileSize();
    const maxFileSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);

    useEffect(() => {
        const fetchApiKeys = async () => {
            try {
                const data = await sttApi.listApiKeys();
                const apiKeys = (data.api_keys || []).filter(k => k.is_active);
                setApiKeys(apiKeys);
                
                if (!hasInitialized.current) {
                    hasInitialized.current = true;
                }
            } catch (error) {
                console.error("Failed to fetch API keys:", error);
            }
        };

        fetchApiKeys();
        return () => {
            clearInterval(timerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);
    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0];
            if (rejection.errors.some(e => e.code === 'file-too-large')) {
                setUploadError(`File is too large. Maximum file size is ${maxFileSizeMB}MB.`);
            } else if (rejection.errors.some(e => e.code === 'file-invalid-type')) {
                setUploadError('Invalid file type. Please upload an audio or video file.');
            } else {
                setUploadError('File was rejected. Please try again.');
            }
            return;
        }
        
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setUploadError('');
            setSelectedFile(file);
        }
    }, [maxFileSizeMB]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
          'audio/*': ['.mp3', '.wav', '.m4a', '.flac', '.ogg', '.aac'],
          'video/*': ['.mp4', '.avi', '.mov', '.mkv', '.webm']
        },
        multiple: false,
        maxSize: maxFileSize,
        disabled: false,
        noClick: false,
        noKeyboard: false
    });
    
    const handleUpload = async () => {
        setUploadError('');
        if (!selectedFile) {
            setUploadError('Please select a file first.');
            return;
        }
        if (activeJobs >= maxConcurrent) {
            setUploadError(`You have reached the maximum concurrent jobs limit (${maxConcurrent}). Please wait for some jobs to complete.`);
            return;
        }
        if (!apiKeyId) {
            setUploadError('Please select your API key for uploads.');
            return;
        }

        // Quick client-side pre-check against remaining time
        try {
            const [stats, durationSeconds] = await Promise.all([
                sttApi.getUserStats(),
                getMediaDurationSeconds(selectedFile),
            ]);

            const subscriptionRemaining = stats?.subscription?.remaining_minutes;
            const topupRemaining = stats?.top_up_usage?.remaining_minutes;
            const remainingMinutes = subscriptionRemaining ?? topupRemaining ?? null;

            if (remainingMinutes !== null && durationSeconds) {
                const durationMinutes = durationSeconds / 60;
                if (remainingMinutes <= 0) {
                    setUploadError('Not enough remaining time in your plan to process this audio file.');
                    return;
                }
                if (durationMinutes > remainingMinutes) {
                    setUploadError(
                        `Audio file is ${durationMinutes.toFixed(1)} minutes long, but you only have ${remainingMinutes.toFixed(1)} minutes remaining.`
                    );
                    return;
                }
            }
        } catch (error) {
            // If metadata or stats fail, continue with upload and let server validate.
        }
        
        setUploading(true);
        setUploadProgress(0);
        
        // Progress callback for upload
        const onProgress = (progressData) => {
            if (progressData && typeof progressData.percent === 'number') {
                setUploadProgress(progressData.percent);
            }
        };
        
        try {
            await onUpload(selectedFile, apiKeyId, onProgress, wordTimestamps, enableDiarization);
            setSelectedFile(null);
            setUploadProgress(0);
        } catch (error) {
            const errorMessage = error.response?.data?.detail || error.message || 'Upload failed';
            setUploadError(getErrorMessage(errorMessage));
            setUploadProgress(0);
        } finally {
            setUploading(false);
        }
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
        setUploadError('');
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: mimeType });
                const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
                setSelectedFile(new File([blob], `recording.${ext}`, { type: mimeType }));
                setUploadError('');
                stream.getTracks().forEach((t) => t.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
        } catch (err) {
            if (!window.isSecureContext) {
                setUploadError('Microphone requires HTTPS. Use localhost or a secure URL.');
            } else if (err.name === 'NotAllowedError') {
                setUploadError('Microphone access was denied. Please allow microphone permission and try again.');
            } else {
                setUploadError('Microphone is not available. Please check your browser permissions.');
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    return (
        <Paper variant="outlined" sx={{ p: 4, mb: 4 }} data-test-id="file-upload">
            <Typography variant="h6" gutterBottom>
                Transcribe
            </Typography>

            <Box sx={{ mb: 3 }}>
                <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="api-key-select-label">API Key</InputLabel>
                    <Select
                        labelId="api-key-select-label"
                        id="api-key-select"
                        value={apiKeyId ?? ""}
                        label="API Key"
                        onChange={(e) => {
                            const v = e.target.value;
                            onApiKeyIdChange(v === "" ? null : Number(v));
                        }}
                        inputProps={{ 'data-test-id': 'file-upload-api-key-select' }}
                    >
                        {apiKeys.length === 0 ? (
                            <MenuItem value="" disabled>
                                No API keys found. Please create one in the dashboard.
                            </MenuItem>
                        ) : (
                            apiKeys.map((key) => (
                                <MenuItem key={key.id} value={key.id}>
                                    {key.name}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={wordTimestamps}
                            onChange={(e) => setWordTimestamps(e.target.checked)}
                            size="small"
                            inputProps={{ 'data-test-id': 'file-upload-word-timestamps' }}
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">Word-level timestamps</Typography>
                            <Tooltip title="Include precise start/end time and confidence for each individual word in the result.">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Tooltip>
                        </Box>
                    }
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={enableDiarization}
                            onChange={(e) => setEnableDiarization(e.target.checked)}
                            size="small"
                            inputProps={{ 'data-test-id': 'file-upload-enable-diarization' }}
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">Speaker diarization</Typography>
                            <Tooltip title="Identify and label individual speakers in the audio. Each segment will include a speaker label (SPEAKER_00, SPEAKER_01, ...).">
                                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Tooltip>
                        </Box>
                    }
                />
            </Box>
            <Box
                {...getRootProps()}
                data-test-id="file-upload-dropzone"
                sx={{
                pointerEvents: 'auto',
                border: '2px dashed',
                borderColor: isDragActive 
                    ? 'primary.main' 
                    : selectedFile 
                        ? (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300'
                        : (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive 
                    ? (theme) => theme.palette.mode === 'dark' ? 'action.selected' : 'primary.50'
                    : selectedFile 
                        ? (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
                        : 'transparent',
                transition: 'all 0.2s',
                opacity: selectedFile ? 0.8 : 1,
                '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'action.selected' : 'primary.50',
                },
                }}
            >
                <input {...getInputProps()} />
                <Box sx={{ mb: 2 }}>
                <CloudUpload 
                    sx={{ 
                    fontSize: 48,
                    color: isDragActive 
                        ? 'primary.main' 
                        : selectedFile 
                            ? (theme) => theme.palette.mode === 'dark' ? 'grey.500' : 'grey.400'
                            : (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300'
                    }} 
                />
                </Box>
                <Box>
                {selectedFile ? (
                    <Box>
                    <Typography variant="body1" fontWeight="medium" color="text.primary">
                        Selected file: {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={(e) => {
                        e.stopPropagation();
                        clearSelectedFile();
                        }}
                        data-test-id="file-upload-remove-btn"
                    >
                        Remove File
                    </Button>
                    </Box>
                ) : isDragActive ? (
                    <Typography variant="body1" color="primary.main" fontWeight="medium">
                    Drop the file here...
                    </Typography>
                ) : (
                    <Box>
                    <Typography variant="body1" fontWeight="medium" color="text.primary">
                        Drag and drop your audio/video file here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        or click to browse
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                        Supports: MP3, WAV, M4A, FLAC, OGG, AAC, MP4, AVI, MOV, MKV, WEBM (max {maxFileSizeMB}MB)
                    </Typography>
                    </Box>
                )}
                </Box>
            </Box>

            {/* Compact record bar */}
            <Box
                sx={{
                    mt: 1.5,
                    px: 2,
                    py: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isRecording ? 'rgba(211,47,47,0.4)' : 'divider',
                    bgcolor: isRecording ? 'rgba(211,47,47,0.05)' : 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    transition: 'all 0.3s',
                }}
            >
                {!isRecording ? (
                    <>
                        <Typography variant="body2" color="text.disabled" sx={{ flex: 1 }}>
                            Or record from microphone
                        </Typography>
                        {window.isSecureContext ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<MicIcon sx={{ fontSize: 16 }} />}
                                onClick={startRecording}
                                disabled={uploading}
                                data-test-id="file-upload-record-btn"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.8rem',
                                    py: 0.5,
                                }}
                            >
                                Record
                            </Button>
                        ) : (
                            <Typography variant="caption" color="text.disabled">
                                HTTPS required
                            </Typography>
                        )}
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: 'error.main',
                                flexShrink: 0,
                                '@keyframes pulse': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.7)' },
                                    '70%': { boxShadow: '0 0 0 7px rgba(211,47,47,0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
                                },
                                animation: 'pulse 1.4s ease-in-out infinite',
                            }}
                        />
                        <Typography variant="body2" color="error.main" fontWeight={600} sx={{ flex: 1 }}>
                            {formatTime(recordingTime)}
                        </Typography>
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<StopIcon sx={{ fontSize: 16 }} />}
                            onClick={stopRecording}
                            data-test-id="file-upload-stop-recording-btn"
                            sx={{ textTransform: 'none', fontSize: '0.8rem', py: 0.5 }}
                        >
                            Stop
                        </Button>
                    </>
                )}
            </Box>

            {uploading && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {uploadProgress > 0 ? 'Uploading file...' : 'Preparing upload...'}
                        </Typography>
                        {uploadProgress > 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {uploadProgress}%
                            </Typography>
                        )}
                    </Box>
                    {uploadProgress > 0 ? (
                        <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            sx={{ height: 8, borderRadius: 1 }}
                        />
                    ) : (
                        <LinearProgress
                            variant="indeterminate"
                            sx={{ height: 8, borderRadius: 1 }}
                        />
                    )}
                </Box>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                variant="contained"
                size="large"
                onClick={handleUpload}
                disabled={!selectedFile || uploading || activeJobs >= maxConcurrent || !apiKeyId}
                data-test-id="file-upload-submit-btn"
                sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'medium',
                    boxShadow: 2,
                    '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s',
                }}
                >
                {uploading && uploadProgress === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Creating Job...
                    </Box>
                ) : uploading ? (
                    'Uploading...'
                ) : (
                    'Transcribe'
                )}
                </Button>
            </Box>
            
            {activeJobs >= maxConcurrent && (
                <Alert severity="error" sx={{ mt: 2 }}>
                Maximum concurrent jobs reached. Please wait for some jobs to complete.
                </Alert>
            )}
            
            {(!apiKeyId) && !selectedFile && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                Please select your API key to create jobs.
                </Alert>
            )}
            
            {uploadError && (
                <Alert
                severity="error"
                sx={{ mt: 2 }}
                onClose={() => setUploadError('')}
                data-test-id="file-upload-error"
                >
                <Typography variant="body2" component="div">
                    <strong>Upload failed:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {uploadError}
                </Typography>
                </Alert>
            )}
        </Paper>
    );
}

export default FileUpload;
